import React, { FC, useContext, useState } from "react";
import Link from "next/link";
import styled, { css } from "styled-components";
import * as Sentry from "@sentry/browser";
import CheckoutContext from "checkout";

import { GRAY_LIGHT, GRAY, RED } from "../styles/constants";
import separateNumber from "../../lib/separate-number";
import { easeIn, easeOut, easeOutFast } from "../styles/atoms";
import useVariants from "../hooks/use-variants";
import SelectionFilter, { Value, Label } from "./SelectionFilter";
import getUndef from "../../lib/get-undef";
import Image, { IImage } from "../common/Image";
import { cover } from "../styles/_fit-images";
import IconTags from "./IconTags";
import { product_shop_collections_products_collections } from "../../__generated__/product";
import { BtnLoading } from "../common/Button";
import getMeasurementUnit from "../../lib/get-measurement-unit";
import AddAdditionsModal from "../сard/AddAdditionsModal";
import { Query } from "react-apollo";
import { toppings, toppingsVariables } from "../../__generated__/toppings";
import { GET_TOPPINGS } from "../../queries/Products";
import { IngredientWithPrice } from "../сard/ProductCard";
import CountConfiguration from "../common/CountConfiguration";
import * as gtag from "../../lib/gtag";
import { withId } from "react-yandex-metrika";
import { CityContext } from "../context/CityContext";
import { ecommerceAddToBasket } from "../../lib/ym-ecommerce";
import KionTag from "./KionTag";
import useShop from "../hooks/use-shop";

export interface ItemProps extends CompactProps {
  slug: string;
  name: string;
  description: string;
  images: any;
  variants: any;
  collections: product_shop_collections_products_collections[];
  slugCollection?: string;
  metafields?: any;
  id?: string;
  isPizzaDay?: boolean | null;
}
interface CompactProps {
  compact?: boolean;
}

export enum Additions {
  Empty = "empty",
  PartiallyFilled = "partially filled",
  Full = "full",
}

const KION_SET_ID = "clpgv2ph84q1j08760kh4qpxr";

const Item: FC<ItemProps> = ({
  slug,
  images,
  name,
  description,
  compact,
  variants,
  collections,
  slugCollection,
  isPizzaDay,
  ...props
}) => {
  const [hasToppings, setHasToppings] = useState(false);
  const [ingredients, setIngredients] = useState<IngredientWithPrice[]>([]);
  const [additionsStatus, setAdditionsStatus] = useState(Additions.Empty);
  const [isAdditionsModalOpen, setIsAdditionsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { addToCart } = useContext(CheckoutContext);
  const { ymAccountId } = useContext(CityContext);
  const href = `/catalog/${slugCollection}/${slug}`;
  const { primaryDomain } = useShop();

  const potato =
    hasToppings && collections.findIndex((c) => c.slug === "potatoes") >= 0;

  const additionsId = getUndef(() => {
    let id;
    collections.forEach(({ metafields }) => {
      id = metafields.find(({ key }) => key === "ingredientsCollection").value;
    });
    return id;
  });

  const multipleRows = Boolean(
    getUndef(
      () =>
        props.metafields.find(({ key }) => key === "multipleRows").value ===
        "Да",
    ),
  );

  const isPizza =
    slugCollection === "pizza" ||
    !!collections.find((collection) => collection.slug === "pizza");

  const isFries = slug === "kartofel-fri";

  const getVariantIndex = (pizzaSize = "31 см"): number => {
    if (!isPizza && !isFries) return 0;

    if (isPizza) {
      const findByPizzaSize = (pizza) => pizza.name === pizzaSize;
      const variantIndex = variants.findIndex(findByPizzaSize);

      return variantIndex === -1 ? 0 : variantIndex;
    }

    if (isFries) {
      const findByWeight = (variant) => variant.name === " 150 гр";
      const variantIndex = variants.findIndex(findByWeight);

      return variantIndex === -1 ? 0 : variantIndex;
    }
  };

  const variantIndex = getVariantIndex();

  const {
    selected: { id, price, weight, weightUnit, priceCompareAt },
    options,
    selectVariant,
  } = useVariants(variants, variantIndex);

  const hasDiscount = Boolean(
    variants.reduce((prev, { priceCompareAt }) => {
      if (priceCompareAt) {
        return (prev += priceCompareAt);
      }
      return prev;
    }, 0),
  );

  const src = images[0] && images[0].url;

  const ingredientsWithoutPrice = ingredients.map(({ id, name, amount }) => ({
    id,
    name,
    amount,
  }));

  const addToBasket = async () => {
    setLoading(true);

    try {
      await addToCart({
        id,
        ingredients: ingredientsWithoutPrice,
      });

      ecommerceAddToBasket({
        id,
        name,
        price,
        category: slugCollection,
        quantity: 1,
      });

      try {
        withId(ymAccountId)("reachGoal", "ADD-TO-CART");
      } catch (error) {
        console.error(error);
      }
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
    }
    setLoading(false);
  };

  const openModal = () => {
    setIsAdditionsModalOpen(true);
  };

  const addToBasketOrOpenModal = () => {
    potato && additionsStatus === Additions.Empty ? openModal() : addToBasket();
  };

  const selectDefault = getUndef(() =>
    (isPizza || isFries) && options[0]?.valuesWithParams?.[variantIndex]
      ? options[0].valuesWithParams[variantIndex].value
      : options[0].valuesWithParams[0].value,
  );

  return (
    <Container {...props} compact={compact}>
      <Link href={`/catalog/[code]/[item]`} as={href}>
        <ItemLink href={href} compact={compact}>
          <ImageWrap compact={compact}>
            {!compact && (
              <IconTags
                small
                collections={collections}
                discount={hasDiscount}
                pizzaDay={!!isPizzaDay}
                productSlug={isPizzaDay ? slug : undefined}
              />
            )}
            {props?.id === KION_SET_ID && <KionTag />}
            <CustomImage
              width={400}
              height={compact ? 120 : 300}
              src={src}
              compact={compact}
              alt=""
            />
          </ImageWrap>
        </ItemLink>
      </Link>
      <Content compact={compact}>
        <Link href={`/catalog/[code]/[item]`} as={href}>
          <ItemLink href={href}>
            <Name compact={compact}>{name}</Name>
            <Description compact={compact}>{description}</Description>
            {!!weight && Number(weight) > 0 && (
              <AmountMobile compact={compact}>
                {separateNumber(weight) + " гр."}
              </AmountMobile>
            )}
          </ItemLink>
        </Link>
        {!!options.length && (
          <ItemSelectionFilter
            isMobile={true}
            multipleRows={multipleRows}
            options={options[0].valuesWithParams.map(({ value }) => ({
              value,
            }))}
            onChange={(value) => {
              selectVariant(options[0].name, value);
            }}
            selectedDefault={selectDefault}
          />
        )}
        <BottomWrapper compact={compact}>
          {!compact && !!additionsId && (
            <Link href={`/catalog/[code]/[item]`} as={href}>
              <ChooseFillingButton>Добавить начинки</ChooseFillingButton>
            </Link>
          )}
          <Bottom compact={compact}>
            <PriceWrap compact={compact}>
              <Price>{price} ₽</Price>
              {priceCompareAt && (
                <ComparePrice>{priceCompareAt} ₽</ComparePrice>
              )}
            </PriceWrap>
            {!!weight && Number(weight) > 0 && !isPizza && (
              <Amount compact={compact}>
                <MeasureIco />
                {separateNumber(weight) + " " + getMeasurementUnit(weightUnit)}
              </Amount>
            )}
            <AddToBasket
              isLoading={loading}
              compact={compact}
              disabled={primaryDomain?.holydays}
              onClick={() => {
                addToBasketOrOpenModal();
              }}
            >
              <AddToBasketInner compact={compact}>
                <span className="btn-text">В корзину</span>
              </AddToBasketInner>
            </AddToBasket>
          </Bottom>
        </BottomWrapper>
      </Content>
      {!!additionsId && (
        <Query<toppings, toppingsVariables>
          query={GET_TOPPINGS}
          variables={{
            id: additionsId,
          }}
          onCompleted={(data) => {
            const hasToppings = getUndef(
              () => data.shop.collections[0].products.length > 0,
            );
            if (hasToppings) {
              setHasToppings(true);
            }
          }}
        >
          {({ loading, error, data }) => {
            if (error || loading) {
              return null;
            }

            return (
              <AddAdditionsModal
                open={isAdditionsModalOpen}
                setOpen={setIsAdditionsModalOpen}
                addToBasket={addToBasket}
                cleanAllChecked={() => setIngredients([])}
                additionsStatus={additionsStatus}
                setAdditionsStatus={setAdditionsStatus}
              >
                <CountConfiguration
                  data={data}
                  checked={ingredients}
                  setChecked={setIngredients}
                  setAdditionsStatus={setAdditionsStatus}
                  isModal
                />
              </AddAdditionsModal>
            );
          }}
        </Query>
      )}
    </Container>
  );
};

interface ICompact {
  compact: boolean;
}

const Container = styled.div<ICompact>`
  display: flex;
  flex-direction: ${({ compact }) => (compact ? "row" : "column")};
  justify-content: space-between;
  border-radius: 20px;
  background-color: #fff;
  overflow: hidden;
  border: ${({ compact }) => (compact ? `1px solid ${GRAY_LIGHT}` : undefined)};
`;

const ItemLink = styled.a<ICompact>`
  display: block;
  flex-basis: ${({ compact }) => (compact ? "110px" : undefined)};
  flex-shrink: 0;
`;

const ImageWrap = styled.div<ICompact>`
  z-index: 1;
  position: relative;
  border-radius: ${({ compact }) => (compact ? undefined : "20px 20px 0 0")};
  overflow: hidden;
`;

const CustomImage = styled(Image)<ICompact>`
  && {
    width: 100%;
    height: ${({ compact }) => (compact ? "120px" : "300px")};
    ${cover}
  }
`;

const ChooseFillingButton = styled.button`
  padding: 12px 0;
  margin: 10px 0;
  cursor: pointer;
  background: #f5f5f5;
  border-radius: 10px;
  font-size: 16px;
  border: tan;
  width: 100%;
`;

const Content = styled.div<ICompact>`
  position: relative;
  z-index: 1;
  padding: ${({ compact }) => (compact ? "14px 14px 22px 5px" : "20px")};
  width: 100%;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: ${({ compact }) => (compact ? undefined : "space-between")};
  /* min-height: ${({ compact }) => (compact ? undefined : "308px")}; */
`;

const clampName = css`
  & {
    overflow: hidden;
    max-height: calc(1.2em * 2);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;
const Name = styled.h4<ICompact>`
  font-size: ${({ compact }) => (compact ? "18px" : undefined)};
  margin-bottom: 3px;
  ${({ compact }) => (compact ? clampName : undefined)}
`;

const Description = styled.p<ICompact>`
  font-size: 13px;
  line-height: 1.6em;
  max-height: calc(1.6em * 2);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 0;
  display: ${({ compact }) => (compact ? "none" : undefined)};
`;

const Bottom = styled.div<ICompact>`
  position: relative;
  display: flex;
  align-items: ${({ compact }) => (compact ? "flex-end" : "center")};
  justify-content: space-between;
  margin-top: ${({ compact }) => (compact ? undefined : "10px")};
`;

const PriceWrap = styled.span<ICompact>`
  display: inline-block;
  flex-shrink: 0;
  line-height: ${({ compact }) => (compact ? 1 : undefined)};
`;

const Price = styled.span`
  display: block;
  font-size: 24px;
  line-height: 0.9;
`;

const ComparePrice = styled.span`
  display: block;
  text-decoration: line-through;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.25);
`;

const Amount = styled.span<ICompact>`
  display: ${({ compact }) => (compact ? "none" : "inline-block")};
  padding-left: 10px;
  border-left: 1px solid ${GRAY_LIGHT};
  margin-right: auto;
  margin-left: 10px;
  font-weight: bold;
  font-size: 12px;
  color: ${GRAY};
`;

const AmountMobile = styled.p<ICompact>`
  display: ${({ compact }) => (compact ? "block" : "none")};
  margin: 0;
  color: ${GRAY};
  font-size: 13px;
  line-height: 1;
`;

const MeasureIco = styled.span`
  display: inline-block;
  width: 11px;
  height: 11px;
  margin-right: 7px;
  transform: translateY(1px);
  background: url("/static/icons/measure.svg") no-repeat center;
  vertical-align: baseline;
  background-size: contain;
`;

const absoluteButton = css`
  & {
    position: absolute;
    right: 0;
    bottom: -14px;
  }
`;

const AddToBasketInner = styled.span<ICompact>`
  display: inline-flex;
  align-items: center;
  height: 46px;
  padding: ${({ compact }) => (compact ? "0" : "16px 0")};
  margin-left: auto;
  background: ${({ compact }) => (compact ? "none" : "#f8f6f6")};
  border-radius: 10px;

  ${easeIn};

  .btn-text {
    display: ${({ compact }) => (compact ? "none" : "inline-block")};
    text-transform: uppercase;
    font-size: 12px;
    font-weight: bold;
    opacity: 0.5;
    margin-right: 10px;
    margin-left: 16px;
  }

  @media (max-width: 1600px) and (min-width: 1481px) {
    .btn-text {
      display: none;
    }
  }

  &:after {
    display: block;
    height: 46px;
    width: 46px;
    border-radius: inherit;
    background: ${RED} url("/static/icons/plus.svg") no-repeat center;
    -webkit-appearance: none;
    -webkit-box-shadow: 0px 6px 12px rgba(230, 25, 23, 0.4);
    box-shadow: 0px 6px 12px rgba(230, 25, 23, 0.4);
    transition: inherit;
    content: "";
  }

  &:hover {
    ${easeOut};
    &:after {
      box-shadow: 0px 6px 12px rgba(230, 25, 23, 0.5);
      transform: translate3d(0, -3px, 0);
    }
  }

  &:active {
    ${easeOutFast};
    &:after {
      box-shadow: 0px 6px 12px rgba(230, 25, 23, 0.3);
      transform: translate3d(0, 0, 0);
    }
  }

  /* @media (max-width: 1600px) {
    .btn-text {
      display: none;
    }
  } */

  @media (max-width: 400px) {
    padding: 0;
  }
`;

interface IAddToBasket extends ICompact {
  isLoading: boolean;
}
const AddToBasket = styled.button<IAddToBasket>`
  display: inline-block;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  ${({ compact }) => (compact ? absoluteButton : undefined)};
  ${({ isLoading }) =>
    isLoading
      ? `
        ${BtnLoading};
        pointer-events: none;
        color: inherit;
        &:after {
          right: 13px;
          margin-left: 0;
          margin-right: 0;
          left: auto;
        }

        ${AddToBasketInner} {
          &:after {
            background-image: none;
            transform: none;
          }
        }
      `
      : undefined};

  &:disabled {
    cursor: not-allowed;
    color: rgba(0, 0, 0, 0.5);

    span span {
      opacity: 1;
    }

    & *::after {
      background-color: #bdbdbd;
      box-shadow: none;
    }

    &:hover *::after {
      transform: none;
    }
  }
`;

const ItemSelectionFilter = styled(SelectionFilter)`
  margin-top: 1em;
  margin-bottom: 0;
  flex-wrap: ${({ multipleRows }) => (multipleRows ? "wrap" : "nowrap")};

  ${Value} {
    font-size: 15px;
  }

  ${Label} {
    flex-grow: 1;
    text-align: center;
  }
`;

const CompactBottomWrapper = css`
  & {
    margin-top: auto;
  }
`;

const BottomWrapper = styled.div<ICompact>`
  ${({ compact }) => (compact ? CompactBottomWrapper : undefined)};
`;

export default Item;
